import { useEffect, useState } from "react";
import type { TurnClock } from "@/features/chat/lib/turnClock";

/**
 * Whole seconds since `startedAt`, ticking once a second; `0` while there is no start.
 *
 * The turn stream carries no timestamps, so the clock the reader sees is the browser's own,
 * started when the question was sent. Passing `null` stops the interval (turn finished).
 *
 * Time spent waiting on a clarification answer is left out (lib/turnClock): `clockPausedAt` holds
 * the reading where the wait began, and `clarificationWaitMs` takes earlier waits off it.
 */
export function useElapsedSeconds(startedAt: number | null, clock: TurnClock = {}): number {
  const waitMs = clock.clarificationWaitMs ?? 0;
  const pausedAt = clock.clockPausedAt ?? null;
  const [elapsed, setElapsed] = useState(() => secondsSince(startedAt, waitMs, pausedAt));

  useEffect(() => {
    if (startedAt === null) {
      setElapsed(0);
      return undefined;
    }
    setElapsed(secondsSince(startedAt, waitMs, pausedAt));
    if (pausedAt !== null) {
      return undefined;
    }
    const interval = window.setInterval(
      () => setElapsed(secondsSince(startedAt, waitMs, null)),
      1000,
    );
    return () => window.clearInterval(interval);
  }, [startedAt, waitMs, pausedAt]);

  return elapsed;
}

function secondsSince(startedAt: number | null, waitMs: number, pausedAt: number | null): number {
  if (startedAt === null) {
    return 0;
  }
  const until = pausedAt ?? Date.now();
  return Math.max(0, Math.floor((until - startedAt - waitMs) / 1000));
}
